import { useEffect, useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { styled as muiStyled } from "@mui/material/styles";

import { Grid, InputAdornment, Stack, Typography } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

import CloseIcon from "@mui/icons-material/Close";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty } from "src/common";
import { Black, Primary } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";
import { UseUpload } from "src/context/UploadProductContext";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import {
  createMasterData,
  findProductTypeById,
  updateMasterData,
} from "src/api/master-data";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperTextField = styled(Stack)`
  width: 100%;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 600;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const TextFieldLabel = styled.p`
  margin: 0;
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
    line-height: 16px;
  }
`;

const StyledTextField = styled(TextField)`
  /* .MuiInputBase-adornedEnd {
    padding-right: 2px;
  } */
  &.MuiTextField-root .MuiOutlinedInput-root.MuiInputBase-multiline {
    padding-right: 14px;
  }
  /* & .MuiInputBase-multiline .MuiInputAdornment-root {
    align-self: flex-end;
    height: 30px;
  } */
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": { borderRadius: 16 },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": { width: "100%", maxWidth: 640 },
  },
  "& .MuiDialogActions-root": { justifyContent: "center" },
  "& .MuiDialogActions-root>:not(:first-of-type)": { marginLeft: 24 },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": { margin: 24 },
    "& .MuiDialogContent-root": { padding: 16, paddingTop: 0 },
    "& .MuiDialogActions-root": { padding: 16 },
  },
}));

const AddTypeDialog = ({ typeId = null, setIsLoading, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog } = useDialog();
  const { masterdata } = UseUpload();

  const defaultData = {
    // _id: null,
    type_en: "",
    type_th: "",
    // icon_img: "",
    category: null, // { _id: "", cate_en: "", cate_th: "" }
    keywords: "", // for internal search
    brand: "",
    meta_tag: {
      title: "",
      desc: "",
      keywords: "",
      // img: "",
    },
    status: "active", // active, inactive
  };
  const [typeData, setTypeData] = useState(defaultData);
  const [isEdit, setIsEdit] = useState(false);

  const handleSelectCategory = async (value) => {
    setTypeData((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const isFormValid = [
    "type_th",
    "type_en",
    "category",
    "brand",
    "keywords",
    "meta_tag",
  ].every((required) => {
    let objVal = typeData[required];

    if (required === "meta_tag")
      return Object.keys(objVal).every((req) => {
        let subObj = objVal[req];
        return checkNotEmpty(subObj);
      });
    return checkNotEmpty(objVal);
  });

  useEffect(() => {
    // console.log("-----typeId----", typeId);
    if (typeId) {
      setIsEdit(true);
      fetchTypeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  const fetchTypeData = async () => {
    try {
      const data = await findProductTypeById(typeId);
      setTypeData({
        ...typeData,
        ...data,
      });
    } catch (err) {
      console.error("find one err:", err);
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        payload: {
          ...typeData,
          ...(!!typeData?._id ? {} : { _id: typeData?._id }),
          category: typeData?.category?._id,
        },
        type: "product_type",
      };

      const res = isEdit
        ? await updateMasterData(typeData?._id, updateParams)
        : await createMasterData(updateParams);

      if (res) {
        enqueueSnackbar(`${isEdit ? "Edit" : "Add"} Product type Success!`, {
          variant: "success",
        });
        await onSuccess();
      }
    } catch (err) {
      console.error("err:", err);
      enqueueSnackbar(err?.response?.data?.message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    limit: 100,
  });

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: { sm: 3, xs: 2 },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: { sm: font.size.lg, xs: font.size.md },
          fontWeight: 600,
          span: { color: Primary, fontWeight: 400 },
        }}
      >
        ข้อมูลประเภทสินค้า
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container rowSpacing={2} columnSpacing={3}>
          <Grid item xs={12} md={6}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>ประเภท (ภาษาไทย)</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                value={typeData?.type_th || ""}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    type_th: e.target.value,
                  }))
                }
                error={!checkNotEmpty(typeData?.type_th)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>ประเภท (ภาษาอังกฤษ)</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                value={typeData?.type_en || ""}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    type_en: e.target.value,
                  }))
                }
                error={!checkNotEmpty(typeData?.type_en)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>หมวดหมู่</TextFieldLabel>
              <Autocomplete
                aria-required={true}
                id="search-user-select"
                isOptionEqualToValue={(option, value) =>
                  option?._id === value?._id
                }
                disableClearable
                options={masterdata.category_data || []}
                value={typeData.category}
                renderOption={(props, option) => (
                  <li {...props} key={`user-select-${option?._id}`}>
                    <Stack>
                      <Typography variant="subtitle1">
                        {option?.cate_en}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: font.size.sm }}
                      >
                        {option?.cate_th}
                      </Typography>
                    </Stack>
                  </li>
                )}
                onChange={(_, newValue) => {
                  handleSelectCategory(newValue);
                }}
                getOptionLabel={(option) =>
                  option?.cate_en
                    ? `${option?.cate_en} - ` + option?.cate_th + ``
                    : ""
                }
                filterOptions={filterOptions}
                fullWidth
                sx={{
                  "&.MuiAutocomplete-root .MuiOutlinedInput-root": {
                    padding: "12px 16px",
                  },
                  ".MuiInputBase-input.MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    placeholder="ค้นหาชื่อหมวดหมู่"
                    // error={!checkNotEmpty(typeData?.email)}
                  />
                )}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>แบรนด์ที่เกี่ยวข้อง</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                value={typeData?.brand}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    brand: e.target.value,
                  }))
                }
                error={!checkNotEmpty(typeData?.brand)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>Keywords คำค้นหาในเว็บ</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                // label="SEO-Keywords"
                value={typeData?.keywords}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    keywords: e.target.value,
                  }))
                }
                error={!checkNotEmpty(typeData?.keywords)}
              />
            </WrapperTextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SpanTitle>SEO - Google</SpanTitle>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>Title</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                // label="SEO-Title"
                value={typeData?.meta_tag.title}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    meta_tag: {
                      ...prev.meta_tag,
                      title: e.target.value,
                    },
                  }))
                }
                inputProps={{ maxLength: 50 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {typeData?.meta_tag.title?.length}/50
                    </InputAdornment>
                  ),
                }}
                error={!checkNotEmpty(typeData?.meta_tag.title)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>Description</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                // label="SEO-Description"
                id="seo-description-textfield"
                multiline
                rows={3}
                value={typeData?.meta_tag.desc}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    meta_tag: {
                      ...prev.meta_tag,
                      desc: e.target.value,
                    },
                  }))
                }
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {typeData?.meta_tag.desc?.length}/100
                    </InputAdornment>
                  ),
                }}
                error={!checkNotEmpty(typeData?.meta_tag.desc)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>Keywords</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                // label="SEO-Keywords"
                placeholder="ตัวอย่าง: ลามิเนต ภายใน คุ้มค่า"
                value={typeData?.meta_tag.keywords}
                onChange={(e) =>
                  setTypeData((prev) => ({
                    ...prev,
                    meta_tag: {
                      ...prev.meta_tag,
                      keywords: e.target.value,
                    },
                  }))
                }
                inputProps={{ maxLength: 50 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {typeData?.meta_tag.keywords?.length}/50
                    </InputAdornment>
                  ),
                }}
                error={!checkNotEmpty(typeData?.meta_tag.keywords)}
              />
            </WrapperTextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          disabled={!isFormValid}
          onClick={() =>
            openDialog({
              type: "confirm",
              title: `คุณต้องการ${isEdit ? "แก้ไข" : "เพิ่ม"}ประเภทสินค้าใช่หรือไม่`,
              onConfirm: () => {
                onSubmit();
              },
              maxWidth: 342,
            })
          }
          fullWidth
          sx={{ maxWidth: 180 }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default AddTypeDialog;
